import { useContext } from "react";
import {
    createFailure,
    DropdownOption,
    getById,
    mapToDropdownOption,
    validationFailed,
    ValidationResult,
    validationSuccessful
} from "sprint-common-web";

import { CreateEntityResponse } from "../../../../../common/api/common/CreateEntityResponse";
import { AspectRatiosDataContext } from "../../../../../common/data/lookup-data-providers/AspectRatiosDataProvider";
import { FramerateProfilesDataContext } from "../../../../../common/data/lookup-data-providers/FramerateProfilesDataProvider";
import { ManzanitaPresetsDataContext } from "../../../../../common/data/lookup-data-providers/ManzanitaPresetsDataProvider";
import { CreateEncodeSpecificationMpeg1Request } from "../../../../../common/api/orders-api/encode-specification/requests/CreateEncodeSpecificationMpeg1Request";
import { createAuditInfo } from "../../../../../common/types/AuditInfo";
import { UserContext } from "../../../../../core/security/UserProvider";
import { useAdminEncodeSpecificationApi } from "../../../api/useAdminEncodeSpecificationApi";
import { EncodeSpecificationContext } from "../../../context/EncodeSpecificationContext";
import {
    AspectType,
    aspectTypesOptions,
    EncodeSpecificationFramerate,
    EncodeSpecificationType,
    FrameratePresets
} from "../../../context/EncodeSpecificationItem";
import { createFrameratePresets } from "../common/createFrameratePresets";
import { useEncodeSpecificationValidator } from "../useEncodeSpecificationValidator";
import { CreateEncodeSpecificationMpeg1FormInputs } from "./CreateEncodeSpecificationMpeg1Form";
import { CreateEncodeSpecificationMpeg4FormInputs } from "./CreateEncodeSpecificationMpeg4Form";

export const useCreateEncodeSpecificationForm = () => {
    const { isNameUnique } = useEncodeSpecificationValidator();
    const { createEncodeSpecificationMpeg1, createEncodeSpecificationMpeg4 } = useAdminEncodeSpecificationApi();

    const { addEncodeSpecificationMpeg1, addEncodeSpecificationMpeg4 } = useContext(EncodeSpecificationContext);
    const { name: userName } = useContext(UserContext);
    const { manzanitaPresets } = useContext(ManzanitaPresetsDataContext);
    const { framerateProfiles } = useContext(FramerateProfilesDataContext);
    const { aspectRatios } = useContext(AspectRatiosDataContext);

    const aspectRatioOptions: DropdownOption[] = aspectRatios.map((item) => mapToDropdownOption(item));
    const framerateProfileOptions: DropdownOption[] = framerateProfiles.map((item) => mapToDropdownOption(item));
    const manzanitaPresetOptions: DropdownOption[] = manzanitaPresets.map((item) => mapToDropdownOption(item));

    const createEncodeSpecMpeg1 = async (inputs: CreateEncodeSpecificationMpeg1FormInputs) => {
        const name = inputs.name;
        const slug = inputs.slug;
        const aspectType = Number(inputs.aspectType);
        const targetAspectRatioId = inputs.targetAspectRatioId;

        const frameratePresets = createFrameratePresets({
            framerateId23_976: inputs["framerate23_976"],
            framerateId24: inputs["framerate24"],
            framerateId25: inputs["framerate25"],
            framerateId29_97: inputs["framerate29_97"],
            framerateId30: inputs["framerate30"],
            framerateId50: inputs["framerate50"],
            framerateId59_94: inputs["framerate59_94"],
            framerateId60: inputs["framerate60"],
            framerateIdOther: inputs["framerateOther"],
            manzanitaPresets
        });

        await createEncodeSpecificationMpeg1(
            getCreateEncodeSpecMpeg1Request(name, frameratePresets, slug, aspectType, targetAspectRatioId)
        ).then((response: CreateEntityResponse) => {
            addEncodeSpecificationMpeg1({
                id: response.entityId,
                type: EncodeSpecificationType.MPEG_1,
                targetAspectRatio: getById(aspectRatios, targetAspectRatioId),
                name,
                frameratePresets,
                audit: createAuditInfo(userName),
                slug,
                aspectType
            });
        });
    };

    const createEncodeSpecMpeg4 = async (inputs: CreateEncodeSpecificationMpeg4FormInputs) => {
        const name = inputs.name;
        const ffmpegCommand = inputs.ffmpegCommand;
        const framerateProfileId = inputs.framerateProfileId;
        const targetAspectRatioId = inputs.targetAspectRatioId;

        await createEncodeSpecificationMpeg4({
            ffmpegCommand,
            name,
            framerateProfileId,
            targetAspectRatioId
        }).then((response: CreateEntityResponse) => {
            addEncodeSpecificationMpeg4({
                id: response.entityId,
                type: EncodeSpecificationType.MPEG_4,
                targetAspectRatio: getById(aspectRatios, targetAspectRatioId),
                name,
                ffmpegCommand,
                framerateProfile: getById(framerateProfiles, inputs.framerateProfileId),
                audit: createAuditInfo(userName)
            });
        });
    };

    const validateNameUnique = async (name: string | undefined): Promise<ValidationResult> => {
        if (name && !isNameUnique(name)) {
            const failure = createFailure(`${name} is not unique name`, "encodeSpec.Name");

            return validationFailed([failure]);
        }

        return validationSuccessful();
    };

    return {
        manzanitaPresetOptions,
        aspectTypesOptions,
        aspectRatioOptions,
        framerateProfileOptions,
        validateNameUnique,
        createEncodeSpecMpeg1,
        createEncodeSpecMpeg4
    };
};

const getCreateEncodeSpecMpeg1Request = (
    name: string,
    frameratePresets: FrameratePresets,
    slug: number,
    aspectType: AspectType,
    targetAspectRatioId: string
): CreateEncodeSpecificationMpeg1Request => {
    const presets: Record<EncodeSpecificationFramerate, string> = {
        "23.976": frameratePresets["23.976"].id,
        "24": frameratePresets["24"].id,
        "25": frameratePresets["25"].id,
        "29.97": frameratePresets["29.97"].id,
        "30": frameratePresets["30"].id,
        "50": frameratePresets["50"].id,
        "59.94": frameratePresets["59.94"].id,
        "60": frameratePresets["60"].id,
        OTHER: frameratePresets.OTHER.id
    };

    return {
        name,
        frameratePresets: presets,
        slug,
        aspectType,
        targetAspectRatioId
    };
};
